'use client';

import { useLanguage } from '@/contexts/language-context';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface MtAccountSelectorProps {
  accounts: string[];
  value: string | null;
  onValueChange: (value: string) => void;
  onUnavailableClick?: () => void;
  disabled?: boolean;
}

export default function MtAccountSelector({ accounts, value, onValueChange, onUnavailableClick, disabled = false }: MtAccountSelectorProps) {
  const { t } = useLanguage();
  const hasAccounts = accounts.length > 0;
  const selectedValue = value && accounts.includes(value) ? value : undefined;
  const hasSelectedAccount = !!selectedValue;
  const accessibleLabel = t('chat.selectTradingAccount');

  return (
    <Select
      value={selectedValue}
      onValueChange={onValueChange}
      onOpenChange={(open) => {
        if (open && !hasAccounts) {
          onUnavailableClick?.();
        }
      }}
      open={hasAccounts ? undefined : false}
      disabled={disabled}>
      <SelectTrigger
        aria-label={accessibleLabel}
        title={accessibleLabel}
        dir="ltr"
        className="w-fit min-w-0 justify-start gap-1 rounded-lg border-transparent bg-transparent px-1 py-0 text-white shadow-none transition-colors data-[size=default]:h-8 hover:bg-white/[0.055] focus-visible:border-white/10 focus-visible:ring-2 focus-visible:ring-[#f98715]/25 disabled:bg-transparent sm:gap-1.5 sm:px-2 sm:data-[size=default]:h-9 [&>svg]:!size-3.5 [&>svg]:text-white/55 sm:[&>svg]:!size-4">
        <span
          className="flex size-4 shrink-0 items-center justify-center sm:size-5"
          aria-hidden="true">
          {/* The supplied SVG is a raster wrapper; use its embedded source directly for consistent browser rendering. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/mt-account-icon.png"
            alt=""
            width="20"
            height="20"
            draggable={false}
            className="block size-4 object-contain sm:size-5"
          />
        </span>
        <span
          className={`min-w-0 shrink truncate text-left text-[11px] font-normal tabular-nums sm:text-sm ${
            hasSelectedAccount ? 'max-w-[3.75rem] text-white/90 sm:max-w-24' : 'max-w-[5.75rem] text-white/55 sm:max-w-28'
          }`}>
          <SelectValue placeholder={t('chat.selectAccount')} />
        </span>
      </SelectTrigger>

      <SelectContent
        align="end"
        sideOffset={6}
        className="z-[130] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border-white/10 bg-[#231f20]/98 p-1.5 text-white shadow-[0_16px_45px_rgba(0,0,0,0.48)] backdrop-blur-xl [&_[data-slot=select-viewport]]:!h-auto [&_[data-slot=select-viewport]]:p-0">
        {accounts.map((account) => (
          <SelectItem
            key={account}
            value={account}
            dir="ltr"
            className="h-9 rounded-lg py-0 pr-8 pl-2.5 text-xs font-normal tabular-nums text-white/80 focus:bg-white/[0.08] focus:text-white data-[state=checked]:bg-[#f98715]/10 data-[state=checked]:text-[#f98715] sm:text-sm">
            {account}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
